import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import 'mapbox-gl/dist/mapbox-gl.css';
import { LngLatLike, Map, MapRef, ViewStateChangeEvent } from 'react-map-gl';
import useMapControls from '../hooks/MapControls';
import MapMarkers from './markers/MapMarkers';
import MapPolygons from './polygons/MapPolygons';
import useViewController from '../hooks/ViewController';
import useMapInfo from '../hooks/MapInfo';
import DrawerContainer from './drawer';
import Actions from './actions/Actions';
import { useDispatch, useSelector } from 'react-redux';
import { MapMode, ResultsType } from '../types';
import SearchFiltersContainer from './common/SearchFiltersContainer';
import MQuickFiltersDrawer from './filters/mobile/MQuickFiltersDrawer';
import OnboardingContainer from './filters/OnboardingContainer';
import {
  setCompoundViewBounds,
  setDrawerOpen,
  setMapMode,
  setSahelMode,
  setResultsType,
  setSelectedArea,
  setSelectedCompound,
  setViewBounds,
  setAreaLoaded,
  setSearchDrawerOpen
} from '../../../redux/slices/mapSlice';
import { isEqual } from '../../../helpers/isEqual';
import ListViewBtn from './common/ListViewBtn';
import {
  SearchContext,
  SearchContextInterface
} from '../../search-new/context/SearchContext';
import {
  getSearchArea,
  getSearchCompound,
  getSearchDeveloper
} from '../../../new-lib/apis';
import ShowResultsBtn from './common/ShowResultsBtn';
import usePolygonType from '../hooks/PolygonTypes';
import LoadingChip from './search/LoadingChip';
import RecenterBtn from './common/RecenterBtn';
import useMapHelpers from '../hooks/MapHelpers';
import DistanceResultModal from './calculate-distance/DistanceResultModal';
import { MapConfigDto } from '../dtos';
import { SearchFiltersActionTypes } from '../../../reducers/search-filters/actions';
import { SearchFiltersTypes } from '../../../reducers/search-filters/reducer';
import { updateQuery } from '../../../helpers/common';
import { useRouter } from 'next/router';
import { setCompounds } from '../../../redux/slices/nawyNowSlice';
import MCalculateDistance from './calculate-distance/mobile/MCalculateDistance';
import { SAHEL_BOUNDS, SAHEL_ZOOM } from '../config/mapConfig';
import ExpandMapIcon from '../../../new-components/svg-icons/ExpandMapIcon';

interface MapContainerProps {
  isSahel?: boolean;
}

const MapContainer = ({ isSahel }: MapContainerProps) => {
  const dispatch = useDispatch();
  const { asPath } = useRouter();
  const isSahelMap = asPath.includes('sahel-map');
  const isNawyNow = asPath.includes('nawy-now');
  const { searchFiltersState, searchFiltersDispatch } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const { isViewBoundsWithinBounds } = useMapHelpers();
  const mapRef = useRef<MapRef>(null);
  const router = useRouter();

  const firstDestination = useSelector(
    (state: any) => state.map.firstDestination
  );
  const secondDestination = useSelector(
    (state: any) => state.map.secondDestination
  );
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const mapMode = useSelector((state: any) => state.map.mapMode, isEqual);
  const resultsType = useSelector((state: any) => state.map.resultsType);
  const mapConfig: MapConfigDto = useSelector((state: any) => state.map.config);
  const selectedCompound = useSelector(
    (state: any) => state.map.selectedCompound
  );
  const drawerOpen = useSelector((state: any) => state.map.drawerOpen);
  const quickFilter = useSelector((state: any) => state.map.quickFilter);
  const drawerFirstOpened = useSelector(
    (state: any) => state.map.drawerFirstOpened
  );
  const { onMapLoad, onClick, recenterMap, isFetching } =
    useMapControls(mapRef);
  const { getPolygonType } = usePolygonType();
  const { interactiveLayerIds } = useMapInfo();
  const [initialDeveloper, setInitialDeveloper] = useState(null);
  useViewController(mapRef);

  const handleRecenter = () => {
    recenterMap();
  };
  useEffect(() => {
    if (!isNawyNow) {
      document.body.style.overflow = 'hidden';
    }
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
      payload: {
        name: SearchFiltersTypes.AREAS,
        value: []
      }
    });
    if (searchFiltersState.searchFilters.areas.length == 1) {
      getSearchArea(searchFiltersState.searchFilters.areas[0]).then(res => {
        if (res?.polygon_points) {
          dispatch(
            setSelectedArea({
              id: res?.id,
              name: res?.name,
              isFiltered: true,
              geometry: {
                type: getPolygonType(res?.polygon_points),
                coordinates: res?.polygon_points
              },
              long: res?.location_point?.[0],
              lat: res?.location_point?.[1]
            })
          );
          dispatch(setResultsType(ResultsType.COMPOUNDS));
          dispatch(setDrawerOpen(true));
        }
      });
    }
    if (searchFiltersState.searchFilters.areas.length > 1) {
      updateQuery(router, undefined, ['areas'], true);
    }
    if (searchFiltersState.searchFilters.developers.length == 1) {
      if (selectedCompound) {
        searchFiltersDispatch({
          type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
          payload: {
            name: SearchFiltersTypes.DEVELOPERS,
            value: []
          }
        });
        updateQuery(router, undefined, ['developers'], true);
      } else {
        getSearchDeveloper(searchFiltersState.searchFilters.developers[0]).then(
          res => {
            setInitialDeveloper(res);
          }
        );
      }
    } else if (searchFiltersState.searchFilters.developers.length > 1) {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: {
          name: SearchFiltersTypes.DEVELOPERS,
          value: []
        }
      });
      updateQuery(router, undefined, ['developers'], true);
    }

    return () => {
      document.body.style.overflow = 'auto';
      dispatch(setSelectedArea(null));
    };
  }, []);

  useEffect(() => {
    if (isNawyNow && searchFiltersState.searchFilters.areas.length > 0) {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: {
          name: SearchFiltersTypes.COMPOUNDS,
          value: []
        }
      });
      if (searchFiltersState.searchFilters.areas.length == 1) {
        getSearchArea(searchFiltersState.searchFilters.areas[0]).then(res => {
          if (res?.polygon_points) {
            dispatch(
              setSelectedArea({
                id: res?.id,
                name: res?.name,
                isFiltered: true,
                geometry: {
                  type: getPolygonType(res?.polygon_points),
                  coordinates: res?.polygon_points
                },
                long: res?.location_point?.[0],
                lat: res?.location_point?.[1]
              })
            );
            dispatch(setResultsType(ResultsType.COMPOUNDS));
          }
        });
      } else {
        setSelectedArea(null);
        dispatch(setResultsType(ResultsType.AREAS));
        handleRecenter();
      }
    } else if (
      searchFiltersState.searchFilters.compounds.length == 0 &&
      searchFiltersState.searchFilters.areas.length == 0 &&
      isNawyNow
    ) {
      setSelectedArea(null);
      dispatch(setResultsType(ResultsType.AREAS));
      handleRecenter();
    }
  }, [searchFiltersState.searchFilters.areas]);

  useEffect(() => {
    if (isNawyNow && searchFiltersState.searchFilters.compounds.length > 0) {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: {
          name: SearchFiltersTypes.AREAS,
          value: []
        }
      });
      if (searchFiltersState.searchFilters.compounds.length == 1) {
        getSearchCompound(searchFiltersState.searchFilters.compounds[0]).then(
          res => {
            if (res?.polygon_points) {
              dispatch(setResultsType(ResultsType.COMPOUNDS));
              dispatch(setMapMode(MapMode.COMPOUND));
              dispatch(setAreaLoaded(true));
              dispatch(
                setSelectedCompound({
                  id: res?.id,
                  name: res?.name,
                  isFiltered: true,
                  geometry: {
                    type: getPolygonType(res?.polygon_points),
                    coordinates: res?.polygon_points
                  },
                  long: res?.location_point?.[0],
                  lat: res?.location_point?.[1]
                })
              );
            }
          }
        );
      } else {
        setSelectedCompound(null);
      }
    }
  }, [searchFiltersState.searchFilters.compounds]);
  const getMaxBounds = useCallback(() => {
    if (isSahelMap) {
      return SAHEL_BOUNDS as [LngLatLike, LngLatLike];
    } else if (
      mapConfig?.sahelData?.calculateDistanceBounds &&
      mapMode == MapMode.CalculateDistance
    ) {
      return [
        {
          lat: mapConfig.sahelData?.calculateDistanceBounds.bottomRight.lat,
          lng: mapConfig.sahelData?.calculateDistanceBounds.topLeft.long
        },
        {
          lat: mapConfig.sahelData?.calculateDistanceBounds.topLeft.lat,
          lng: mapConfig.sahelData?.calculateDistanceBounds.bottomRight.long
        }
      ] as [LngLatLike, LngLatLike];
    }
    return undefined;
  }, [mapMode]);
  const onMoveChange = useCallback(
    (e: ViewStateChangeEvent) => {
      if (!mapConfig || !mapRef.current) {
        return;
      }
      const bounds = mapRef.current.getBounds();
      const topLeft = { lat: bounds?._ne.lat, long: bounds?._sw.lng };
      const bottomRight = { lat: bounds?._sw.lat, long: bounds?._ne.lng };
      if (
        isViewBoundsWithinBounds(
          { topLeft, bottomRight },
          mapConfig?.sahelData?.bounds
        )
      ) {
        dispatch(setSahelMode(true));
      } else {
        dispatch(setSahelMode(false));
        if (mapMode === MapMode.CalculateDistance) {
          dispatch(setMapMode(MapMode.SEARCH));
        }
      }
      if (
        e.viewState.zoom > mapConfig.desktop.areasMaxZoom ||
        mapMode == MapMode.CalculateDistance
      ) {
        dispatch(setResultsType(ResultsType.COMPOUNDS));
        if (!selectedCompound && mapMode != MapMode.CalculateDistance) {
          dispatch(setMapMode(MapMode.SEARCH));
          dispatch(setAreaLoaded(true));
          if (!drawerFirstOpened) {
            dispatch(setDrawerOpen(true));
            dispatch(setSearchDrawerOpen(true));
          }
        }
        dispatch(setCompoundViewBounds({ topLeft, bottomRight }));
      } else {
        if (!isSahelMap) {
          dispatch(setResultsType(ResultsType.AREAS));
        }
        dispatch(setSelectedArea(null));
        if (!asPath.includes('nawy-now')) {
          dispatch(setSelectedCompound(null));
          dispatch(setCompounds([]));
          dispatch(setAreaLoaded(false));
          dispatch(setMapMode(MapMode.SEARCH));
        }
        dispatch(setDrawerOpen(false));
        // if (router.asPath.includes('/nawy-now')) {
        //   searchFiltersDispatch({
        //     type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        //     payload: {
        //       name: SearchFiltersTypes.COMPOUNDS,
        //       value: []
        //     }
        //   });
        //   searchFiltersDispatch({
        //     type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        //     payload: {
        //       name: SearchFiltersTypes.AREAS,
        //       value: []
        //     }
        //   });
        // }
        dispatch(setViewBounds({ topLeft, bottomRight }));
      }
    },
    [
      mapConfig,
      selectedCompound,
      drawerFirstOpened,
      mapMode,
      searchFiltersState.searchFilters.areas,
      searchFiltersState.searchFilters.compounds
    ]
  );

  const handleClick = () => {
    router.push(`/search?view=map`);
  };

  const mapStyle =
    resultsType == ResultsType.AREAS
      ? 'mapbox://styles/mapbox/streets-v12'
      : mapConfig?.styleUri;
  return (
    <Container className="map">
      <Map
        id="map"
        ref={mapRef}
        onMoveEnd={onMoveChange}
        mapStyle={mapStyle}
        mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
        onLoad={onMapLoad}
        onClick={onClick}
        interactiveLayerIds={interactiveLayerIds}
        minZoom={isSahelMap ? SAHEL_ZOOM : 1}
        maxZoom={mapConfig?.maxZoom}
        dragRotate={false}
        pitchWithRotate={false}
        touchZoomRotate={true}
        pitch={0}
        bearing={0}
        maxBounds={getMaxBounds()}
      >
        <MapMarkers mapRef={mapRef} />

        <MapPolygons />
        {!isNawyNow && (
          <>
            {mapMode !== MapMode.CalculateDistance && (
              <>
                <Actions />
                <SearchFiltersContainer
                  handleRecenter={handleRecenter}
                  initialDeveloper={initialDeveloper}
                  isSahel={isSahel}
                />
                {mapMode !== MapMode.COMPOUND && <ListViewBtn />}
                {resultsType == ResultsType.COMPOUNDS && (
                  <LoadingChip isFetching={isFetching} />
                )}
                <RecenterBtn handleClick={handleRecenter} />
              </>
            )}
            {!isMobile && firstDestination && secondDestination && (
              <DistanceResultModal />
            )}
            {isMobile && mapMode === MapMode.CalculateDistance && (
              <MCalculateDistance isSahel={isSahel} />
            )}
            {!isMobile && quickFilter === 'nawy_now' && <OnboardingContainer />}

            {!isMobile && resultsType == ResultsType.COMPOUNDS && (
              <ShowResultsBtn />
            )}
            {drawerOpen && <DrawerContainer isSahel={isSahel} />}

            {quickFilter && <MQuickFiltersDrawer />}
          </>
        )}
      </Map>

      {isNawyNow && (
        <button className="map-expand-button" onClick={handleClick}>
          <ExpandMapIcon />
        </button>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: calc(100vh - 70px);
  /* max-height: 95vh; */
  display: flex;
  align-items: center;
  overflow-y: hidden;

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    height: calc(100dvh - 60px);
    max-height: 100vh;
  }

  .mapboxgl-ctrl {
    pointer-events: none;
    z-index: 1;
    display: none;
  }
`;

export default MapContainer;
